/* WHAT GHL WILL NOT LET BRILL BUILD, HANDED OVER WELL (her ask, 2026-10-12:
 * "the workflow outreach to template and newsletter to campaign").
 *
 * GHL's API (OpenAPI, read 2026-10-12) can LIST campaigns and workflows and
 * add a contact to one — it cannot CREATE either. So Brill makes what it can
 * (each email as a template, api/_lib/ghlJobs/email.js) and writes the rest
 * as steps she follows in GHL: a workflow BUILD SHEET for an Outreach
 * sequence, the campaign steps for a Newsletter email. Both are plain text,
 * so they work for anyone — copy-out without GHL is her hard rule.
 *
 * And ONE sender for every "Send to GHL" email button, so the single email,
 * the whole sequence and the campaign handoff can never send differently.
 */

import { channelDef } from "./outreach";
import { emailHtml, emailPlain, GHL_FIRST_NAME, personaliseEmail } from "./emailHtml";

/** The template name GHL files an email under — the server adds "Brill · ". */
export const touchTemplateName = (sequenceName, touch) => `${String(sequenceName || "").trim() || "Outreach"} · Touch ${touch?.position || ""}`.trim();
export const templateTitle = (name) => `Brill · ${String(name || "").replace(/\s+/g, " ").trim() || "Untitled email"}`;

/** A touch's email as the parts the email builders read. */
export const touchParts = (touch) => ({
  subject: String(touch?.subject || "").trim(), preview: "",
  sections: [{ heading: "", text: String(touch?.body || "").trim() }], ask: "", fullVersion: "",
});

/**
 * Send one email to GHL as a template. Returns the job's answer:
 * { ok, name, updated } or { ok: false, error }. Never throws.
 */
export async function sendEmailTemplate({ token, name, format, parts: raw, brand = {}, picture = null }) {
  try {
    /* Into GHL, the reader's name is GHL's own token — every send path
       (one email, or a whole sequence) comes through here. */
    const parts = personaliseEmail(raw, { to: GHL_FIRST_NAME, signer: brand.signer });
    const plain = emailPlain(parts);
    const res = await fetch("/api/ghl", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ job: "email", name, format, html: format === "designed" ? emailHtml(parts, { brand, picture }) : "", plain, preview: parts.preview || "" }),
    });
    const r = await res.json().catch(() => ({}));
    return r.ok ? r : { ok: false, error: r.error || "It didn't reach GHL — try again in a moment." };
  } catch {
    return { ok: false, error: "Couldn't reach GHL just then. Try again in a moment." };
  }
}

/* What each channel is in a GHL workflow — the action she adds for it. */
const ACTION = {
  email: "Send Email",
  text: "Send SMS",
  linkedin: "Manual Action — send on LinkedIn",
  instagram: "Instagram DM (or a Manual Action)",
  facebook: "Facebook Messenger (or a Manual Action)",
};

/**
 * THE WORKFLOW BUILD SHEET for an Outreach sequence — step by step, in her
 * order, as she would build it in GHL. Waits are hers to choose (her note on
 * the mockup: "no cadence bar, no send times"), so the sheet says where they
 * go and not how long. Touches with no words are named as still to write,
 * never skipped — an empty step is a hole in the workflow.
 */
export function workflowSheet(sequence, touches = []) {
  const name = String(sequence?.name || "").trim() || "Untitled sequence";
  const lines = [
    `GHL WORKFLOW — ${name}`,
    "Automation → Workflows → Create Workflow → Start from scratch.",
    "Trigger: whatever starts this conversation for you (a tag, a form, a booked call).",
    "",
  ];
  const ordered = [...touches].sort((a, b) => (a.position || 0) - (b.position || 0));
  ordered.forEach((t, i) => {
    const ch = channelDef(t.channel);
    const words = String(t.body || "").trim();
    if (i > 0) lines.push("   ↓ Wait — as long as feels natural before the next one", "");
    lines.push(`Step ${i + 1} · ${ACTION[ch.id] || "Choose a channel for this touch in Brill"}`);
    if (!words) lines.push("   Not written yet in Brill — write it before building this step.");
    else if (ch.id === "email") {
      lines.push(`   Template: ${templateTitle(touchTemplateName(name, t))}`);
      if (String(t.subject || "").trim()) lines.push(`   Subject: ${String(t.subject).trim()}`);
    } else {
      lines.push("   Message:", ...words.split(/\r?\n/).map((l) => `   ${l}`));
    }
    lines.push("");
  });
  lines.push("Publish the workflow when every step is in. Nothing sends until you do.");
  return lines.join("\n");
}

/** The campaign steps for a Newsletter email GHL now holds as a template. */
export function campaignSteps({ name, subject = "", preview = "" }) {
  return [
    "Marketing → Emails → Campaigns → Create Campaign.",
    `Choose the template "${templateTitle(name)}" (My Templates).`,
    subject ? `Subject: ${subject}` : "Subject: add one — Brill's email has none yet.",
    preview ? `Preview text: ${preview}` : null,
    "Pick your list, then Send or Schedule. Nothing sends until you do.",
  ].filter(Boolean);
}
